<template>
  <div class="flex flex-col justify-between min-h-screen px-2 pb-4 md:pt-4 sm:px-6 lg:px-8">
    <div v-if="!checked" class="container w-full mx-auto my-auto">
      <div class="w-32 h-32 mx-auto ease-linear border-8 rounded-full loader"></div>

      <div class="w-full mt-6 text-xl font-semibold tracking-widest text-center uppercase text-blue-brand-600">
        Iniciando...
      </div>
    </div>

    <div v-else class="flex flex-col justify-around h-full">
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <img class="w-auto mx-auto h-36" :src="require('../assets/logo.png')" alt="ColEscBa" />
      </div>

      <h1 class="text-2xl font-bold leading-tight text-center text-cool-gray-600">{{ title }}</h1>

      <p class="mt-2 font-medium text-center text-gray-500">
        Por favor, proporcione los datos solicitados que serán verificados en la base de datos de la Caja de Seguridad Social para que
        pueda ver su credencial y las de su grupo familiar.
      </p>

      <div class="flex flex-wrap justify-center mt-4 md:justify-start">
        <div class="w-full px-3">
          <label for="dni" class="block ml-1 text-sm font-semibold text-cool-gray-700 lg:text-base">
            DNI
          </label>

          <div class="mt-1 rounded-md shadow-sm">
            <input
              id="dni"
              type="number"
              placeholder="DNI Númerico. Ej. '37058719'"
              class="block w-full px-3 py-2 mt-2 transition duration-300 ease-in-out border rounded-md appearance-none placeholder-cool-gray-400 focus:outline-none sm:text-sm sm:leading-5"
              :class="[
                {
                  'border-red-500 text-red-500 focus:border-red-500 focus:shadow-outline-red': $v.dni.$error,
                },
                {
                  'border-green-500 text-green-500 focus:border-green-500 focus:shadow-outline-green': !$v.dni.$invalid,
                },
                {
                  'hover:border-cool-gray-500 border-cool-gray-300 focus:shadow-outline-blue focus:border-blue-300': !$v.dni.$dirty,
                },
              ]"
              v-model="dni"
              @input="$v.dni.$touch()"
            />
          </div>
          <p v-if="$v.dni.$error" class="mt-2 text-xs font-bold text-red-600">
            El DNI debe tener entre 7 y 8 dígitos
          </p>
        </div>

        <div class="mx-3 mt-4 text-center md:mt-0">
          <label for="fechaNacimiento" class="ml-1 text-sm font-semibold text-cool-gray-700 lg:text-base">
            <span>Fecha de nacimiento</span>
            <span class="ml-1 text-xs font-normal">(día/mes/año)</span>
          </label>

          <div class="flex items-center justify-center mt-2 text-lg">
            <input
              id="dia"
              type="number"
              min="1"
              max="31"
              placeholder="1"
              class="w-12 px-2 py-2 mx-2 text-center transition duration-300 ease-in-out border rounded-md appearance-none placeholder-cool-gray-400 focus:outline-none"
              :class="[
                {
                  'border-red-500 text-red-500 focus:border-red-500 focus:shadow-outline-red': $v.dia.$error,
                },
                {
                  'border-green-500 text-green-500 focus:border-green-500 focus:shadow-outline-green': !$v.dia.$invalid,
                },
                {
                  'hover:border-cool-gray-500 border-cool-gray-300 focus:shadow-outline-blue focus:border-blue-300': !$v.dia.$dirty,
                },
              ]"
              v-model="dia"
              @input="$v.fechaNacimientoGroup.$touch()"
            />

            <input
              id="mes"
              type="number"
              min="1"
              max="12"
              placeholder="11"
              class="w-12 px-2 py-2 mx-2 text-center transition duration-300 ease-in-out border rounded-md appearance-none placeholder-cool-gray-400 focus:outline-none"
              :class="[
                {
                  'border-red-500 text-red-500 focus:border-red-500 focus:shadow-outline-red': $v.mes.$error,
                },
                {
                  'border-green-500 text-green-500 focus:border-green-500 focus:shadow-outline-green': !$v.mes.$invalid,
                },
                {
                  'hover:border-cool-gray-500 border-cool-gray-300 focus:shadow-outline-blue focus:border-blue-300': !$v.mes.$dirty,
                },
              ]"
              v-model="mes"
              @input="$v.fechaNacimientoGroup.$touch()"
            />

            <input
              id="anio"
              type="number"
              :min="minYear"
              :max="maxYear"
              placeholder="1992"
              class="w-20 px-2 py-2 mx-2 text-center transition duration-300 ease-in-out border rounded-md appearance-none placeholder-cool-gray-400 focus:outline-none"
              :class="[
                {
                  'border-red-500 text-red-500 focus:border-red-500 focus:shadow-outline-red': $v.anio.$error,
                },
                {
                  'border-green-500 text-green-500 focus:border-green-500 focus:shadow-outline-green': !$v.anio.$invalid,
                },
                {
                  'hover:border-cool-gray-500 border-cool-gray-300 focus:shadow-outline-blue focus:border-blue-300': !$v.anio.$dirty,
                },
              ]"
              v-model="anio"
              @input="$v.fechaNacimientoGroup.$touch()"
            />
          </div>

          <p v-if="$v.fechaNacimientoGroup.$error" class="mt-2 text-xs font-bold text-red-600">
            La fecha ingresada no es válida
          </p>
        </div>

        <div class="mt-6 md:mt-0">
          <span class="inline-flex">
            <button
              class="inline-flex items-center p-3 font-bold border focus:outline-none"
              :class="
                sexo == 'M'
                  ? 'text-white bg-blue-brand-500 border-blue-brand-500 hover:bg-blue-brand-700 hover:border-blue-brand-700'
                  : 'text-blue-brand-500 bg-white border-blue-brand-500 hover:bg-blue-brand-700 hover:text-white hover:border-blue-brand-700'
              "
              @click="sexo = 'M'"
            >
              <svg class="-ml-0.5 mr-2 w-6 h-6 fill-current" viewBox="0 0 499.996 499.996">
                <path
                  d="M499.292 19.184c-.1-.528-.384-1.036-.548-1.54l-.332-.908a19.092 19.092 0 00-.664-1.936c-.26-.624-.596-1.204-.92-1.784l-.468-.848a14.69 14.69 0 00-.784-1.388 23.585 23.585 0 00-6.492-6.488c-.424-.284-.876-.516-1.324-.748l-.76-.412c-.628-.356-1.256-.7-1.94-.992a16.729 16.729 0 00-1.776-.58l-.932-.28a13.98 13.98 0 00-1.672-.468c-1.3-.256-2.668-.508-4.1-.536-.16-.016-.344-.136-.464-.136h-97.44c-12.912 0-23.412 10.528-23.412 23.436 0 12.912 10.5 23.436 23.412 23.436h40.928L315.208 151.476c-34.208-26.868-76.764-41.584-120.24-41.584C87.46 109.888 0 197.372 0 304.88c0 107.5 87.46 194.976 194.968 194.976s194.972-87.46 194.972-194.968c0-43.472-14.544-86.024-41.412-120.236L453.124 80.276v40.928c0 12.908 10.528 23.412 23.436 23.412s23.436-10.5 23.436-23.412V23.768c.004-1.516-.4-3.06-.704-4.584zm-304.324 433.86c-81.692 0-148.152-66.46-148.152-148.148 0-81.696 66.46-148.156 148.152-148.156 81.688 0 148.156 66.46 148.156 148.156-.004 81.688-66.468 148.148-148.156 148.148z"
                />
              </svg>
              Masculino
            </button>
          </span>

          <span class="inline-flex">
            <button
              class="inline-flex items-center p-3 font-bold border rounded-r focus:outline-none"
              :class="
                sexo == 'F'
                  ? 'text-white bg-blue-500 border-blue-brand-500 hover:bg-blue-brand-700 hover:border-blue-brand-700'
                  : 'text-blue-brand-500 bg-white border-blue-brand-500 hover:bg-blue-brand-700 hover:text-white hover:border-blue-brand-700'
              "
              @click="sexo = 'F'"
            >
              <svg class="-ml-0.5 mr-2 w-6 h-6 fill-current" viewBox="0 0 500.004 500.004">
                <path
                  d="M304.84.002c-107.608 0-195.16 87.548-195.16 195.16 0 43.516 14.772 86.112 41.664 120.352l-65.76 65.768L40 335.69c-4.424-4.428-10.312-6.864-16.572-6.864s-12.14 2.432-16.568 6.864A23.29 23.29 0 000 352.258c0 6.256 2.436 12.144 6.864 16.564l45.588 45.592-45.588 45.588C2.44 464.422 0 470.31 0 476.57c0 6.256 2.436 12.144 6.864 16.564 4.424 4.428 10.308 6.868 16.568 6.868s12.144-2.436 16.572-6.868l45.584-45.584 45.592 45.584c4.42 4.428 10.304 6.868 16.568 6.868 6.256 0 12.14-2.436 16.568-6.868a23.258 23.258 0 006.868-16.564c0-6.26-2.436-12.148-6.868-16.568l-45.588-45.588 65.76-65.76c34.244 26.892 76.836 41.664 120.356 41.664 107.612 0 195.16-87.552 195.16-195.156C500 87.55 412.452.002 304.84.002zm0 343.452c-81.768 0-148.296-66.52-148.296-148.292s66.528-148.3 148.296-148.3c81.772 0 148.3 66.528 148.3 148.3s-66.528 148.292-148.3 148.292z"
                />
              </svg>
              Femenino
            </button>
          </span>
        </div>

        <p v-if="error" class="mt-3 text-xs font-bold text-center text-red-500">
          Los datos ingresados no se corresponden con un afiliado
        </p>
      </div>

      <div class="flex justify-center mt-6">
        <span class="block w-3/4 rounded-md shadow-sm md:w-1/2">
          <button
            v-if="$v.$invalid"
            class="flex justify-center w-full py-2 font-semibold tracking-wider uppercase border border-transparent rounded-md cursor-not-allowed bg-cool-gray-300 focus:outline-none focus:border-red-500 focus:shadow-outline-red"
          >
            Ingresar
          </button>

          <button
            v-else
            type="submit"
            class="flex justify-center w-full py-2 font-semibold tracking-wider uppercase transition duration-150 ease-in-out border border-transparent rounded-md focus:outline-none"
            @click="login"
            :disabled="handlingLogin"
            :class="
              handlingLogin
                ? 'spinner cursor-not-allowed bg-cool-gray-300'
                : 'cursor-pointer bg-blue-brand-600 text-white hover:bg-blue-brand-500 active:bg-blue-brand-700 focus:border-blue-brand-700 focus:shadow-outline-blue'
            "
          >
            {{ handlingLogin ? 'Ingresando...' : 'Ingresar' }}
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { required, numeric, minLength, maxLength, between } from 'vuelidate/lib/validators'
import moment from 'moment'

import { mixins } from '@colescba/componentes-web'

import * as auth from '@/auth'

const { emitLogin, onChecking } = auth

const minYear = 1900
const maxYear = moment().year()

export default {
  props: {
    title: { type: String, required: true },
  },

  mixins: [mixins.SendsToasts],

  data() {
    return {
      checked: true,
      sexo: '',
      dni: '',
      dia: '',
      mes: '',
      anio: '',
      handlingLogin: false,
      error: false,
      minYear: minYear,
      maxYear: maxYear,
    }
  },

  mounted() {
    onChecking(({ checked }) => (this.checked = checked))
  },

  validations: {
    dni: {
      required,
      numeric,
      minLength: minLength(7),
      maxLength: maxLength(8),
    },

    dia: {
      required,
      numeric,
      between: between(1, 31),
      minLength: minLength(1),
      maxLength: maxLength(2),
    },

    mes: {
      required,
      numeric,
      between: between(1, 12),
      minLength: minLength(1),
      maxLength: maxLength(2),
    },

    anio: {
      required,
      numeric,
      minLength: minLength(4),
      maxLength: maxLength(4),
      between: between(minYear, maxYear),
    },

    fechaNacimiento: {
      required,
      isDate: value => value.isValid(),
    },

    sexo: {
      required,
      isSexo: value => ['F', 'M'].includes(value),
    },

    fechaNacimientoGroup: ['dia', 'mes', 'anio', 'fechaNacimiento'],
  },

  computed: {
    fechaNacimiento() {
      return moment(`${this.dia}-${this.mes}-${this.anio}`, 'DD-MM-Y')
    },
  },

  methods: {
    login() {
      if (this.handlingLogin) return

      this.error = false
      this.handlingLogin = true
      const form = { dni: this.dni, sexo: this.sexo }

      this.$http
        .post('/afiliados/find', form)
        .then(response => this.validateLogin(response.data))
        .catch(error => this.handleErrorResponse(error))
        .finally(() => (this.handlingLogin = false))
    },

    handleErrorResponse(error) {
      if (error.response && error.response.status === 404) {
        this.error = true
        return
      }

      throw error
    },

    async validateLogin(afiliado) {
      if (!this.fechaNacimiento.isSame(moment(afiliado.fechaNacimiento, 'DD-MM-YYYY'))) {
        this.error = true
        return
      }

      const response = await this.$http.get(`/credenciales/${afiliado.contrato}`)
      afiliado.credenciales = response.data.credenciales

      afiliado.credencial = afiliado.credenciales.find(each => each.afiliado.integrante == afiliado.integrante)

      emitLogin({ afiliado })
    },
  },
}
</script>
